import { supabase } from "../../Services/supabaseClient";

const normalizeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const computeLineTotal = (item) => {
  const quantity = normalizeNumber(item.quantity);
  const unitPrice = normalizeNumber(item.unit_price);
  const discountPercent = normalizeNumber(item.discount_percent);
  const taxPercent = normalizeNumber(item.tax_percent);

  const gross = quantity * unitPrice;
  const discountAmount = gross * (discountPercent / 100);
  const taxableBase = gross - discountAmount;
  const taxAmount = taxableBase * (taxPercent / 100);
  return taxableBase + taxAmount;
};

const computeQuoteTotals = (items) => {
  return items.reduce(
    (accumulator, item) => {
      const quantity = normalizeNumber(item.quantity);
      const unitPrice = normalizeNumber(item.unit_price);
      const discountPercent = normalizeNumber(item.discount_percent);
      const taxPercent = normalizeNumber(item.tax_percent);

      const gross = quantity * unitPrice;
      const discountAmount = gross * (discountPercent / 100);
      const taxableBase = gross - discountAmount;
      const taxAmount = taxableBase * (taxPercent / 100);
      const lineTotal = taxableBase + taxAmount;

      return {
        subtotal: accumulator.subtotal + gross,
        discount_total: accumulator.discount_total + discountAmount,
        tax_total: accumulator.tax_total + taxAmount,
        total: accumulator.total + lineTotal,
      };
    },
    { subtotal: 0, discount_total: 0, tax_total: 0, total: 0 }
  );
};

export const getQuotes = async (companyId) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .select(
      `
      *,
      crm_accounts(id, name),
      crm_contacts(id, full_name),
      crm_opportunities(id, name)
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

export const getQuoteById = async (companyId, quoteId) => {
  const { data: quote, error: quoteError } = await supabase
    .from("crm_quotes")
    .select(
      `
      *,
      crm_accounts(id, name, email, phone),
      crm_contacts(id, full_name, email, phone),
      crm_opportunities(id, name)
    `
    )
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .single();

  if (quoteError) throw quoteError;

  const { data: items, error: itemsError } = await supabase
    .from("crm_quote_items")
    .select("*")
    .eq("quote_id", quoteId)
    .order("line_no", { ascending: true });

  if (itemsError) throw itemsError;

  return {
    quote,
    items: items || [],
  };
};

export const createQuote = async ({
  companyId,
  ownerId,
  accountId,
  contactId,
  opportunityId,
  issueDate,
  validUntil,
  currencyCode,
  notes,
  terms,
  items,
}) => {
  const cleanedItems = (items || []).map((item, index) => ({
    line_no: index + 1,
    item_name: item.item_name || "Item",
    description: item.description || "",
    quantity: normalizeNumber(item.quantity),
    unit_price: normalizeNumber(item.unit_price),
    discount_percent: normalizeNumber(item.discount_percent),
    tax_percent: normalizeNumber(item.tax_percent),
    line_total: computeLineTotal(item),
  }));

  const totals = computeQuoteTotals(cleanedItems);

  const quoteNumber = `QT-${Date.now()}`;

  const { data: quote, error: quoteError } = await supabase
    .from("crm_quotes")
    .insert([
      {
        company_id: companyId,
        owner_id: ownerId,
        account_id: accountId || null,
        contact_id: contactId || null,
        opportunity_id: opportunityId || null,
        quote_number: quoteNumber,
        issue_date: issueDate || null,
        valid_until: validUntil || null,
        currency_code: currencyCode || "NGN",
        subtotal: totals.subtotal,
        discount_total: totals.discount_total,
        tax_total: totals.tax_total,
        total: totals.total,
        notes: notes || null,
        terms: terms || null,
        status: "draft",
      },
    ])
    .select()
    .single();

  if (quoteError) throw quoteError;

  if (cleanedItems.length > 0) {
    const itemRows = cleanedItems.map((item) => ({
      quote_id: quote.id,
      line_no: item.line_no,
      item_name: item.item_name,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      discount_percent: item.discount_percent,
      tax_percent: item.tax_percent,
      line_total: item.line_total,
    }));

    const { error: itemsInsertError } = await supabase
      .from("crm_quote_items")
      .insert(itemRows);

    if (itemsInsertError) throw itemsInsertError;
  }

  return quote;
};

export const updateQuoteStatus = async (companyId, quoteId, status) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .update({ status })
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateQuoteDetails = async (companyId, quoteId, payload) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .update(payload)
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const prepareQuoteSend = async ({
  companyId,
  quoteId,
  recipientEmail,
  emailSubject,
  emailBody,
}) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .update({
      recipient_email: recipientEmail || null,
      email_subject: emailSubject || null,
      email_body: emailBody || null,
      status: "sent",
      sent_at: new Date().toISOString(),
    })
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const markQuoteAccepted = async (companyId, quoteId) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .update({
      status: "accepted",
      accepted_at: new Date().toISOString(),
    })
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const markQuoteRejected = async (companyId, quoteId) => {
  const { data, error } = await supabase
    .from("crm_quotes")
    .update({
      status: "rejected",
      rejected_at: new Date().toISOString(),
    })
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const convertQuoteToInvoice = async (companyId, quoteId, userId) => {
  if (!userId) {
    throw new Error("A valid user is required to convert quote to invoice.");
  }
  const { data: quote, error: quoteError } = await supabase
    .from("crm_quotes")
    .select(
      `
      *,
      crm_accounts(id, name, email, phone, customer_id)
    `
    )
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .single();

  if (quoteError) {
    throw quoteError;
  }

  const { data: items, error: itemsError } = await supabase
    .from("crm_quote_items")
    .select("*")
    .eq("quote_id", quoteId)
    .order("line_no", { ascending: true });

  if (itemsError) {
    throw itemsError;
  }

  let linkedCustomerId = quote.crm_accounts?.customer_id || null;

  if (!linkedCustomerId) {
    const { data: customer, error: customerError } = await supabase
      .from("customers")
      .insert([
        {
          company_id: quote.company_id,
          company_name: quote.crm_accounts?.name || "Unnamed Customer",
          email: quote.crm_accounts?.email || null,
          phone: quote.crm_accounts?.phone || null,
        },
      ])
      .select()
      .single();

    if (customerError) {
      throw customerError;
    }

    linkedCustomerId = customer.id;

    const { error: crmAccountUpdateError } = await supabase
      .from("crm_accounts")
      .update({
        customer_id: linkedCustomerId,
      })
      .eq("id", quote.account_id)
      .eq("company_id", companyId);

    if (crmAccountUpdateError) {
      throw crmAccountUpdateError;
    }
  }

  const invoiceItems = (items || []).map((item) => ({
    description: item.item_name,
    details: item.description || "",
    quantity: Number(item.quantity || 0),
    rate: Number(item.unit_price || 0),
    discount_percent: Number(item.discount_percent || 0),
    tax_percent: Number(item.tax_percent || 0),
    amount: Number(item.line_total || 0),
  }));

  const invoicePayload = {
    company_id: quote.company_id,
    user_id: userId,
    account_id: quote.account_id || null,
    quote_id: quote.id,
    opportunity_id: quote.opportunity_id || null,
    customer_id: linkedCustomerId,
    total: Number(quote.total || 0),
    subtotal: Number(quote.subtotal || 0),
    vat_amount: Number(quote.tax_total || 0),
    discount_amount: Number(quote.discount_total || 0),
    items: invoiceItems,
    status: "draft",
  };

  console.log("Invoice payload:", invoicePayload);

  const { data: invoice, error: invoiceError } = await supabase
    .from("invoices")
    .insert([invoicePayload])
    .select()
    .single();

  if (invoiceError) {
    throw invoiceError;
  }

  const { data: updatedQuote, error: quoteUpdateError } = await supabase
    .from("crm_quotes")
    .update({
      status: "converted",
      invoice_id: invoice.id,
    })
    .eq("company_id", companyId)
    .eq("id", quoteId)
    .select()
    .single();

  if (quoteUpdateError) {
    console.error("Quote update error:", quoteUpdateError);
    throw new Error(
      quoteUpdateError.message ||
        "Failed to update quote after invoice creation"
    );
  }

  return {
    invoice,
    quote: updatedQuote,
  };
};
